/** Formatting helpers shared across the app. */

/** VND amount → compact billions string, e.g. 38936924643 → "38.94B". */
export function formatBillion(amount: number): string {
  if (!amount || !isFinite(amount)) return '0';
  return `${(amount / 1_000_000_000).toFixed(2)}B`;
}

/** ISO date → short "DD/MM" (workday rows, calendars). */
export function formatDayMonth(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** ISO date → weekday short label (Mon..Sun). */
export function weekdayShort(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getDay()];
}

/**
 * Money → grouped currency string in the record's currency (default VND).
 * VND uses vi-VN grouping (dot thousands, no decimals): 1500000 → "1.500.000 ₫".
 * Other ISO codes use en-US with 2 decimals: (1500, 'USD') → "$1,500.00".
 * Pass the customer/record's `currency` field; omit it for the VND default.
 */
export function formatMoney(
  amount: number | null | undefined,
  currency: string | null | undefined = 'VND',
): string {
  if (amount == null || !isFinite(amount)) return '—';
  const code = (currency || 'VND').toUpperCase();
  const locale = code === 'VND' ? 'vi-VN' : 'en-US';
  const fractionDigits = code === 'VND' ? 0 : 2;
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: code,
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(amount);
  } catch {
    // Unknown / non-ISO currency code → grouped number + raw code suffix.
    return `${amount.toLocaleString(locale)} ${code}`;
  }
}

/** ISO date → "DD/MM/YYYY". */
export function formatDate(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

/** ISO datetime → "DD/MM/YYYY HH:mm". */
export function formatDateTime(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return `${formatDate(iso)} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** ISO date → "MM/YYYY". */
export function formatMonthYear(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

const VN_WEEKDAY = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'];

/** ISO datetime → "T3 10/12 08:30" (VN weekday + DD/MM + HH:mm) for trip badges. */
export function formatWeekdayDateTime(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${VN_WEEKDAY[d.getDay()]} ${dd}/${mm} ${hh}:${mi}`;
}

/** ISO start date → seniority string, e.g. "3y 2m". */
export function seniorityFrom(startIso?: string | null): string {
  if (!startIso) return '—';
  const start = new Date(startIso);
  if (isNaN(start.getTime())) return '—';
  const now = new Date();
  let months = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
  if (now.getDate() < start.getDate()) months -= 1;
  if (months < 0) months = 0;
  const y = Math.floor(months / 12);
  const m = months % 12;
  return [y ? `${y}y` : '', m ? `${m}m` : ''].filter(Boolean).join(' ') || '0m';
}
